// ─────────────────────────────────────────────────────────────────────────────
//  Attendance report catalog
//
//  Builds a list of "report definitions" derived from the calculated attendance
//  rows (+ leave data). Each definition exposes:
//    - id / label / description / icon / group
//    - columns: [{ key, header, align, width, value(row), tone(row)? }]
//    - rows:    pre-built array of row objects
//
//  The same `columns[].value(row)` is used both for on-screen rendering and for
//  CSV / Excel / PDF export, so what you see is exactly what you export.
// ─────────────────────────────────────────────────────────────────────────────
import { format, parseISO } from 'date-fns';
import {
  FileText, AlarmClock, LogOut, UserX, Plus, AlertTriangle, BarChart3, Plane,
} from 'lucide-react';
import { fmtHours, fmtMinutes } from '../../utils/attendanceUtils';

// ── Label maps (French defaults — translatable later) ────────────────────────
const STATUS_LABELS = {
  present: 'Présent',
  absent: 'Absent',
  absence_justifiee: 'Absence justifiée',
  late: 'Retard',
  early_departure: 'Départ anticipé',
  late_early_departure: 'Retard + départ anticipé',
  missing_exit: 'Sortie manquante',
  no_schedule: 'Sans planning',
  public_holiday: 'Jour férié',
  day_off: 'Repos',
};

const ABSENCE_TYPE_LABELS = {
  paid_leave: 'Congé payé',
  unpaid_leave: 'Congé sans solde',
  sick_leave: 'Maladie',
  training: 'Formation',
  business_trip: 'Mission',
  maternity: 'Maternité',
  paternity: 'Paternité',
  bereavement: 'Décès',
  hajj: 'Pèlerinage',
  other: 'Autre',
};

const ABSENCE_STATUS_LABELS = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Rejeté',
  cancelled: 'Annulé',
};

const statusLabel = (s) => STATUS_LABELS[s] || s || '—';

// ── Shared cell helpers ──────────────────────────────────────────────────────
const fmtDate = (d, locale) =>
  d ? format(parseISO(d + 'T00:00:00'), 'EEE dd/MM/yyyy', { locale }) : '—';

const nameCols = () => [
  { key: 'registration_number', header: 'Matricule', align: 'left', width: 20, value: (r) => r.registration_number || '—' },
  { key: 'last_name', header: 'Nom', align: 'left', width: 30, value: (r) => r.last_name || '' },
  { key: 'first_name', header: 'Prénom', align: 'left', width: 26, value: (r) => r.first_name || '' },
];

// ── Worked-hours display (mirrors the main report logic) ─────────────────────
const workedHoursValue = (r, absenceReasons) => {
  const ar = r.absence_reason_id ? absenceReasons.find((x) => x.id === r.absence_reason_id) : null;
  const isAbsent = r.status === 'absent' || r.status === 'absence_justifiee';
  if (r.status === 'public_holiday') return r.expected_hours != null ? fmtHours(r.expected_hours) : '—';
  if (ar && isAbsent) return ar.is_paid ? fmtHours(r.expected_hours) : '0h';
  return r.worked_hours != null ? fmtHours(r.worked_hours) : '—';
};

// ─────────────────────────────────────────────────────────────────────────────
//  Main builder
// ─────────────────────────────────────────────────────────────────────────────
export function buildReportDefinitions({
  results = [],
  absenceReasons = [],
  congesData = [],
  dateLocale,
}) {
  const sortByNameDate = (a, b) => {
    const na = `${a.last_name} ${a.first_name}`;
    const nb = `${b.last_name} ${b.first_name}`;
    if (na !== nb) return na.localeCompare(nb, 'fr');
    return (a.date || '').localeCompare(b.date || '');
  };

  // ── 1. Full report (current behaviour) ──────────────────────────────────────
  const fullColumns = [
    { key: 'date', header: 'Date', align: 'left', width: 22, value: (r) => fmtDate(r.date, dateLocale) },
    ...nameCols(),
    { key: 'schedule_name', header: 'Horaire', align: 'left', width: 28, value: (r) => r.schedule_name || '—' },
    { key: 'expected_start', header: 'Début', align: 'center', width: 15, value: (r) => r.expected_start || '—' },
    { key: 'expected_end', header: 'Fin', align: 'center', width: 15, value: (r) => r.expected_end || '—' },
    { key: 'actual_entry', header: 'Entrée', align: 'center', width: 15, value: (r) => r.actual_entry || '—' },
    { key: 'actual_exit', header: 'Sortie', align: 'center', width: 15, value: (r) => r.actual_exit || '—' },
    {
      key: 'lateness', header: 'Retard', align: 'center', width: 16,
      value: (r) => (r.lateness_minutes > 0 ? `+${fmtMinutes(r.lateness_minutes)}` : '0'),
      tone: (r) => (r.lateness_minutes > 0 ? 'danger' : 'success'),
    },
    {
      key: 'early', header: 'Dép. ant.', align: 'center', width: 16,
      value: (r) => (r.early_departure_minutes > 0 ? `-${fmtMinutes(r.early_departure_minutes)}` : '0'),
      tone: (r) => (r.early_departure_minutes > 0 ? 'warning' : 'success'),
    },
    { key: 'worked', header: 'Hr Trav.', align: 'center', width: 16, value: (r) => workedHoursValue(r, absenceReasons) },
    {
      key: 'overtime', header: 'H Sup', align: 'center', width: 14,
      value: (r) => (r.overtime_hours > 0 ? `+${fmtHours(r.overtime_hours)}` : '—'),
      tone: (r) => (r.overtime_hours > 0 ? 'accent' : 'muted'),
    },
    { key: 'status', header: 'Statut', align: 'left', width: 24, value: (r) => statusLabel(r.status) },
  ];

  // ── 2. Retardataires ────────────────────────────────────────────────────────
  const latenessRows = results.filter((r) => r.lateness_minutes > 0).sort(sortByNameDate);
  const latenessColumns = [
    { key: 'date', header: 'Date', align: 'left', width: 24, value: (r) => fmtDate(r.date, dateLocale) },
    ...nameCols(),
    { key: 'schedule_name', header: 'Horaire', align: 'left', width: 30, value: (r) => r.schedule_name || '—' },
    { key: 'expected_start', header: 'Entrée prévue', align: 'center', width: 22, value: (r) => r.expected_start || '—' },
    { key: 'actual_entry', header: 'Entrée réelle', align: 'center', width: 22, value: (r) => r.actual_entry || '—' },
    {
      key: 'lateness', header: 'Retard', align: 'center', width: 20,
      value: (r) => `+${fmtMinutes(r.lateness_minutes)}`, tone: () => 'danger',
    },
  ];

  // ── 3. Départs anticipés ────────────────────────────────────────────────────
  const earlyRows = results.filter((r) => r.early_departure_minutes > 0).sort(sortByNameDate);
  const earlyColumns = [
    { key: 'date', header: 'Date', align: 'left', width: 24, value: (r) => fmtDate(r.date, dateLocale) },
    ...nameCols(),
    { key: 'schedule_name', header: 'Horaire', align: 'left', width: 30, value: (r) => r.schedule_name || '—' },
    { key: 'expected_end', header: 'Sortie prévue', align: 'center', width: 22, value: (r) => r.expected_end || '—' },
    { key: 'actual_exit', header: 'Sortie réelle', align: 'center', width: 22, value: (r) => r.actual_exit || '—' },
    {
      key: 'early', header: 'Départ anticipé', align: 'center', width: 24,
      value: (r) => `-${fmtMinutes(r.early_departure_minutes)}`, tone: () => 'warning',
    },
  ];

  // ── 4. Absents ──────────────────────────────────────────────────────────────
  const absentRows = results
    .filter((r) => r.status === 'absent' || r.status === 'absence_justifiee')
    .sort(sortByNameDate);
  const absentColumns = [
    { key: 'date', header: 'Date', align: 'left', width: 24, value: (r) => fmtDate(r.date, dateLocale) },
    ...nameCols(),
    {
      key: 'kind', header: 'Type', align: 'left', width: 26,
      value: (r) => (r.status === 'absence_justifiee' ? 'Justifiée' : 'Non justifiée'),
      tone: (r) => (r.status === 'absence_justifiee' ? 'accent' : 'danger'),
    },
    {
      key: 'reason', header: 'Motif', align: 'left', width: 34,
      value: (r) => r.absence_reason_name || '—',
    },
    {
      key: 'lost', header: 'Heures perdues', align: 'center', width: 24,
      value: (r) => (r.expected_hours != null ? fmtHours(r.expected_hours) : '—'),
    },
  ];

  // ── 5. Heures supplémentaires ───────────────────────────────────────────────
  const overtimeRows = results.filter((r) => r.overtime_hours > 0).sort(sortByNameDate);
  const overtimeColumns = [
    { key: 'date', header: 'Date', align: 'left', width: 24, value: (r) => fmtDate(r.date, dateLocale) },
    ...nameCols(),
    { key: 'worked', header: 'H. travaillées', align: 'center', width: 24, value: (r) => (r.worked_hours != null ? fmtHours(r.worked_hours) : '—') },
    { key: 'expected', header: 'H. prévues', align: 'center', width: 22, value: (r) => (r.expected_hours != null ? fmtHours(r.expected_hours) : '—') },
    {
      key: 'overtime', header: 'Heures sup.', align: 'center', width: 22,
      value: (r) => `+${fmtHours(r.overtime_hours)}`, tone: () => 'accent',
    },
  ];

  // ── 6. Anomalies / pointages incomplets ─────────────────────────────────────
  const anomalyRows = results
    .filter((r) => r.status === 'missing_exit' || r.status === 'no_schedule')
    .sort(sortByNameDate);
  const anomalyColumns = [
    { key: 'date', header: 'Date', align: 'left', width: 24, value: (r) => fmtDate(r.date, dateLocale) },
    ...nameCols(),
    { key: 'anomaly', header: 'Anomalie', align: 'left', width: 30, value: (r) => statusLabel(r.status), tone: () => 'danger' },
    { key: 'actual_entry', header: 'Entrée', align: 'center', width: 18, value: (r) => r.actual_entry || '—' },
    { key: 'actual_exit', header: 'Sortie', align: 'center', width: 18, value: (r) => r.actual_exit || '—' },
  ];

  // ── 7. Synthèse de présence par employé ─────────────────────────────────────
  const summaryMap = {};
  results.forEach((r) => {
    if (!summaryMap[r.employee_id]) {
      summaryMap[r.employee_id] = {
        employee_id: r.employee_id,
        registration_number: r.registration_number,
        last_name: r.last_name,
        first_name: r.first_name,
        present: 0, absent: 0, late: 0, early: 0,
        worked: 0, expected: 0,
      };
    }
    const s = summaryMap[r.employee_id];
    if (r.status === 'present') s.present++;
    else if (r.status === 'absent' || r.status === 'absence_justifiee') s.absent++;
    else if (r.status === 'late') { s.late++; s.present++; }
    else if (r.status === 'early_departure') { s.early++; s.present++; }
    else if (r.status === 'late_early_departure') { s.late++; s.early++; s.present++; }
    else if (r.status === 'missing_exit') s.present++;
    s.worked += r.worked_hours || 0;
    s.expected += r.expected_hours || 0;
  });
  const summaryRows = Object.values(summaryMap)
    .map((s) => {
      const workdays = s.present + s.absent;
      return { ...s, workdays, rate: workdays > 0 ? Math.round((s.present / workdays) * 100) : null };
    })
    .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`, 'fr'));
  const summaryColumns = [
    ...nameCols(),
    { key: 'workdays', header: 'Jours ouvrés', align: 'center', width: 20, value: (r) => String(r.workdays) },
    { key: 'present', header: 'Présents', align: 'center', width: 18, value: (r) => String(r.present), tone: () => 'success' },
    { key: 'absent', header: 'Absents', align: 'center', width: 18, value: (r) => String(r.absent), tone: (r) => (r.absent > 0 ? 'danger' : 'muted') },
    { key: 'late', header: 'Retards', align: 'center', width: 18, value: (r) => String(r.late), tone: (r) => (r.late > 0 ? 'warning' : 'muted') },
    { key: 'worked', header: 'H. travaillées', align: 'center', width: 24, value: (r) => fmtHours(r.worked) },
    { key: 'expected', header: 'H. prévues', align: 'center', width: 22, value: (r) => fmtHours(r.expected) },
    {
      key: 'rate', header: "Taux d'assiduité", align: 'center', width: 26,
      value: (r) => (r.rate != null ? `${r.rate}%` : '—'),
      tone: (r) => (r.rate == null ? 'muted' : r.rate >= 95 ? 'success' : r.rate >= 80 ? 'warning' : 'danger'),
    },
  ];

  // ── 8. État des congés ──────────────────────────────────────────────────────
  const congesRows = [...congesData].sort((a, b) => {
    const na = a.employee_name || '';
    const nb = b.employee_name || '';
    if (na !== nb) return na.localeCompare(nb, 'fr');
    return (a.start_date || '').localeCompare(b.start_date || '');
  });
  const congesColumns = [
    { key: 'employee_name', header: 'Employé', align: 'left', width: 40, value: (r) => r.employee_name || '—' },
    { key: 'type', header: 'Type', align: 'left', width: 28, value: (r) => ABSENCE_TYPE_LABELS[r.absence_type] || r.absence_type || '—' },
    { key: 'start_date', header: 'Du', align: 'center', width: 24, value: (r) => fmtDate(r.start_date, dateLocale) },
    { key: 'end_date', header: 'Au', align: 'center', width: 24, value: (r) => fmtDate(r.end_date, dateLocale) },
    { key: 'days', header: 'Jours', align: 'center', width: 16, value: (r) => (r.days_count != null ? String(r.days_count) : '—') },
    {
      key: 'status', header: 'Statut', align: 'left', width: 24,
      value: (r) => ABSENCE_STATUS_LABELS[r.status] || r.status || '—',
      tone: (r) => (r.status === 'approved' ? 'success' : r.status === 'pending' ? 'warning' : r.status === 'rejected' ? 'danger' : 'muted'),
    },
  ];

  return [
    { id: 'full', label: 'Rapport complet', description: 'Toutes les lignes de pointage de la période', icon: FileText, group: 'Détail', columns: fullColumns, rows: [...results].sort(sortByNameDate), emptyText: 'Aucun enregistrement.' },
    { id: 'lateness', label: 'Retardataires', description: 'Employés arrivés après l’heure (au-delà de la tolérance)', icon: AlarmClock, group: 'Détail', columns: latenessColumns, rows: latenessRows, emptyText: 'Aucun retard sur la période. 🎉' },
    { id: 'early', label: 'Départs anticipés', description: 'Sorties avant l’heure prévue', icon: LogOut, group: 'Détail', columns: earlyColumns, rows: earlyRows, emptyText: 'Aucun départ anticipé sur la période.' },
    { id: 'absent', label: 'Absents', description: 'Absences justifiées et non justifiées', icon: UserX, group: 'Détail', columns: absentColumns, rows: absentRows, emptyText: 'Aucune absence sur la période. 🎉' },
    { id: 'overtime', label: 'Heures supplémentaires', description: 'Heures travaillées au-delà de l’horaire prévu', icon: Plus, group: 'Détail', columns: overtimeColumns, rows: overtimeRows, emptyText: 'Aucune heure supplémentaire sur la période.' },
    { id: 'anomalies', label: 'Anomalies / pointages incomplets', description: 'Sorties manquantes, employés sans planning', icon: AlertTriangle, group: 'Détail', columns: anomalyColumns, rows: anomalyRows, emptyText: 'Aucune anomalie détectée. 🎉' },
    { id: 'summary', label: 'Synthèse de présence', description: 'Récapitulatif par employé avec taux d’assiduité', icon: BarChart3, group: 'Synthèse', columns: summaryColumns, rows: summaryRows, emptyText: 'Aucune donnée.' },
    { id: 'conges', label: 'État des congés', description: 'Congés et absences planifiés sur la période', icon: Plane, group: 'Synthèse', columns: congesColumns, rows: congesRows, emptyText: 'Aucun congé sur la période.' },
  ];
}
